import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnlineOrderFulfillment from '../components/OnlineOrderFulfillment';
import { printOrder } from '../utils/printOrder';
import { matchesOrderPaymentFilter } from '../utils/orderPaymentFilter';
import { matchesOrderDestinationFilter } from '../utils/orderDestinationFilter';
import { orderOutletFinancials } from '../utils/orderOutletFinancials';
import { fetchCatalogBarcodes } from '../features/inventory/stockManagerInventorySlice';
import { API_BASE_URL } from '../utils/apiConfig';
import { useDispatch, useSelector } from 'react-redux';
import {
  Edit3,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import StockManagerLayout from '../components/StockManagerLayout';
import OrderPaymentStatus from '../components/OrderPaymentStatus';
import EditOrderPaymentModal from '../components/EditOrderPaymentModal';
import {
  deletePOSOrder,
  rollbackPOSOrder,
  deletePOSOrderItem,
  fetchPOSOrderDetails,
  fetchPOSOrders,
  updatePOSOrder,
} from '../features/orders/orderSlice';
import { fetchAllProducts, fetchAllProductsFresh } from '../features/products/productSlice';
import OrderProductSuggestions from '../components/OrderProductSuggestions';
import {
  APPROVED_DISCOUNT_MESSAGE,
  calculateOrderDiscount,
  getOrderDiscountSummary,
  MAX_ORDER_DISCOUNT_PERCENT,
  ORDER_DISCOUNT_ROLES,
} from '../utils/orderDiscount';

const asArray = (value) => (Array.isArray(value) ? value : []);
const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getItemId = (item) => item?._id || item?.itemId || item?.id || '';

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const parseWeight = (weight) => {
  const raw = String(weight || '').trim();
  const match = raw.match(/^([\d.]+)\s*(.*)$/);
  if (!match) return { quantity: 0, units: '', weight: raw };

  return {
    quantity: Number(match[1] || 0),
    units: (match[2] || '').trim(),
    weight: raw,
  };
};

const findCatalogMatch = (item, catalog) => {
  const productId = item?.productId || item?.productID || '';
  const brandId = item?.brandId || item?.brandID || '';
  const financialId = item?.financialId || item?.financialID || '';
  const itemName = normalizeText(item?.name || item?.item);
  const itemPrice = Number(item?.price ?? item?.pricePerQty ?? item?.dprice ?? 0);
  const parsedWeight = parseWeight(item?.weight);

  return catalog.find((catalogItem) => {
    const idsMatch =
      productId &&
      catalogItem.productId === productId &&
      (!brandId || catalogItem.brandId === brandId) &&
      (!financialId || catalogItem.financialId === financialId);

    if (idsMatch) return true;

    const catalogName = normalizeText(catalogItem.name);
    const namesMatch =
      itemName &&
      (catalogName.includes(itemName) || itemName.includes(catalogName));
    const pricesMatch =
      !itemPrice || Number(catalogItem.price || 0) === itemPrice;
    const weightsMatch =
      !parsedWeight.quantity ||
      (Number(catalogItem.quantity || 0) === parsedWeight.quantity &&
        normalizeText(catalogItem.units) === normalizeText(parsedWeight.units));

    return namesMatch && pricesMatch && weightsMatch;
  });
};

const normalizeDetailItems = (order, catalog = []) => {
  const sourceItems = asArray(order?.orderItems).length
    ? asArray(order?.orderItems)
    : asArray(order?.items);

  return sourceItems.map((item, index) => {
    const catalogMatch = findCatalogMatch(item, catalog);
    const identityMatch = catalog.find(pack =>
      String(pack.productId) === String(item.productId || item.productID || '') &&
      (!item.brandId || String(pack.brandId) === String(item.brandId)) &&
      (!item.financialId || String(pack.financialId) === String(item.financialId)));
    const qty = Number(item?.qty ?? item?.quantityOrdered ?? 1);
    const price = Number(item?.price ?? item?.pricePerQty ?? item?.dprice ?? 0);
    const parsedWeight = parseWeight(item?.weight);
    const catalogQty = Number(
      item?.quantity ??
        item?.catalogQuantity ??
        catalogMatch?.quantity ??
        parsedWeight.quantity ??
        0
    );
    const units = item?.units || catalogMatch?.units || parsedWeight.units || '';
    const labelWeight = item?.weight || [catalogQty || '', units].filter(Boolean).join(' ');

    return {
      ...item,
      product_barcode_id: item.product_barcode_id || item.catalogProductBarcodeId || identityMatch?.product_barcode_id,
      mk_barcode: item.mk_barcode || identityMatch?.mk_barcode,
      teluguname: item.teluguname || identityMatch?.teluguname,
      _rowKey: getItemId(item) || `row-${index}`,
      _id: getItemId(item),
      name: item?.name || item?.item || catalogMatch?.name || '',
      category: item?.category || catalogMatch?.category || '',
      quantity: catalogQty,
      units,
      weight: labelWeight,
      brand: item?.brand || catalogMatch?.brand || '',
      qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
      image: item?.image || catalogMatch?.image || '',
      price: price || catalogMatch?.price || 0,
      product_code: item?.product_code || item?.productCode || catalogMatch?.product_code || '',
      productId: item?.productId || item?.productID || catalogMatch?.productId || '',
      brandId: item?.brandId || item?.brandID || catalogMatch?.brandId || '',
      financialId: item?.financialId || item?.financialID || catalogMatch?.financialId || '',
    };
  });
};

const buildPayloadItems = (items) =>
  items.map((item) => ({
    ...(item._id ? { _id: item._id } : {}),
    name: item.name,
    category: item.category,
    quantity: Number(item.quantity || 0),
    units: item.units,
    brand: item.brand,
    qty: Number(item.qty || 0),
    image: item.image || '',
    price: Number(item.price || 0),
    product_code: item.product_code || '',
    productId: item.productId,
    brandId: item.brandId,
    financialId: item.financialId,
  }));

const flattenProducts = (products, location) =>
  asArray(products).flatMap((product) =>
    asArray(product?.details).flatMap((detail) =>
      orderOutletFinancials(asArray(detail?.financials), location).map((financial) => ({
        key: [product?._id, detail?._id, financial?._id].filter(Boolean).join(':'),
        name: product?.name || '',
        category: product?.category || '',
        brand: detail?.brand || '',
        productId: product?._id || '',
        brandId: detail?._id || '',
        financialId: financial?._id || '',
        product_barcode_id: financial?.product_barcode_id || financial?.catalogProductBarcodeId || financial?.mkid,
        mk_barcode: financial?.mk_barcode,
        teluguname: product?.teluguname,
        quantity: Number(financial?.quantity || 0),
        units: financial?.units || '',
        price: Number(financial?.dprice || financial?.price || 0),
        mrp: Number(financial?.price || 0),
        product_code: product?.product_code || product?.productCode || '',
        image: detail?.images?.[0]?.image || '',
        stock: Number(financial?.countInStock || 0),
        location: financial?.location || '',
      }))
    )
  );

const OrderManagementPage = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.posUser?.userInfo);
  const token = useSelector((state) => state.posUser?.userInfo?.token);
  const [editingPayment, setEditingPayment] = useState(false);
  const [printLanguage, setPrintLanguage] = useState('english');
  const printCatalog = useSelector(state => state.stockManagerInventory?.catalogBarcodes || []);
  const [printCatalogLoading, setPrintCatalogLoading] = useState(false);
  useEffect(() => {
    if (printLanguage !== 'telugu' || !token) {
      setPrintCatalogLoading(false);
      return;
    }
    let active = true;
    setPrintCatalogLoading(true);
    Promise.resolve(dispatch(fetchCatalogBarcodes())).finally(() => { if (active) setPrintCatalogLoading(false); });
    return () => { active = false; };
  }, [dispatch, token, printLanguage]);
  const [savingDiscount,setSavingDiscount]=useState(false);
  const navigate = useNavigate();
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState('');
  const orders = useSelector((state) => state.orders?.posOrdersList || []);
  const ordersLoading = useSelector((state) => state.orders?.posOrdersListLoading);
  const ordersError = useSelector((state) => state.orders?.posOrdersListError);
  const loadedQuery = useSelector((state) => state.orders?.posOrdersListQuery);
  const selectedOrder = useSelector((state) => state.orders?.posOrderDetails);
  const detailsLoading = useSelector((state) => state.orders?.posOrderDetailsLoading);
  const saving = useSelector((state) => state.orders?.posOrderMutationLoading);
  const products = useSelector((state) => state.products?.all || []);
  const productsLoading = useSelector(state => state.products?.loading);
  const productsError = useSelector(state => state.products?.error);

  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    customerNumber: '',
    posUser: '',
    location: '',
    paymentStatus: '',
    destinationType: '',
  });
  const visibleOrders = useMemo(
    () => orders.filter(order => matchesOrderPaymentFilter(order, filters.paymentStatus)
      && matchesOrderDestinationFilter(order, filters.destinationType)),
    [orders, filters.paymentStatus, filters.destinationType]
  );
  const [draftItems, setDraftItems] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('0');
  const [discountMessage, setDiscountMessage] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const canApplyOrderDiscount = ORDER_DISCOUNT_ROLES.includes(
    String(userInfo?.role || '').toUpperCase()
  );

  const orderOutlet = selectedOrder?.posLocation || userInfo?.location || '';
  const catalog = useMemo(() => flattenProducts(products, orderOutlet), [products, orderOutlet]);


  const selectedTotal = useMemo(
    () =>
      draftItems.reduce(
        (sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0),
        0
      ),
    [draftItems]
  );
  const selectedDiscount = useMemo(
    () => calculateOrderDiscount(selectedTotal, discountPercentage, selectedOrder?.dispatchOrderId != null),
    [discountPercentage, selectedTotal,selectedOrder?.dispatchOrderId]
  );
  const getDisplayedOrderDiscount = useCallback(
    (order) => getOrderDiscountSummary(order),
    []
  );

  const loadOrders = useCallback(async () => {
    const hasDateRange = filters.fromDate || filters.toDate;
    const hasOtherFilters =
      filters.customerNumber.trim() || filters.posUser.trim() || filters.location.trim();

    if (hasDateRange && (!filters.fromDate || !filters.toDate)) {
      alert('Select both From Date and To Date.');
      return;
    }

    const mode = hasDateRange ? 'custom' : hasOtherFilters ? 'filter' : 'today';

    await dispatch(
      fetchPOSOrders({
        mode,
        from: filters.fromDate,
        to: filters.toDate,
        customerNumber: filters.customerNumber.trim(),
        phone: filters.customerNumber.trim(),
        posUser: filters.posUser.trim(),
        location: filters.location.trim(),
      })
    ).unwrap();
  }, [dispatch, filters]);

  const ordersHeading = useMemo(() => {
    if (loadedQuery?.mode === 'custom') return 'Orders by date range';
    if (loadedQuery?.mode === 'filter') return 'Filtered orders';
    return "Today's orders";
  }, [loadedQuery]);

  const searchPending = ['fromDate', 'toDate', 'customerNumber', 'posUser', 'location'].some(key => {
    const queryKey = key === 'fromDate' ? 'from' : key === 'toDate' ? 'to' : key;
    return filters[key].trim() !== String(loadedQuery?.[queryKey] || '').trim();
  });

  useEffect(() => {
    dispatch(fetchPOSOrders({ mode: 'today' }));
  }, [dispatch]);

  useEffect(() => {
    if (token) dispatch(fetchAllProducts(token));
  }, [dispatch, token]);

  useEffect(() => {
    setDraftItems(normalizeDetailItems(selectedOrder, catalog));
    setRemarks('');
    setDiscountPercentage(String(selectedOrder?.discountPercentage || 0));
    setDiscountMessage('');
  }, [selectedOrder, catalog]);

  const openOrder = async (id) => {
    await dispatch(fetchPOSOrderDetails(id)).unwrap();
  };

  const updateItem = (index, field, value) => {
    setDraftItems((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addCatalogItem = (item) => {
    setDraftItems((items) => [
      ...items,
      {
        _rowKey: `new-${Date.now()}`,
        _id: '',
        name: item.name,
        category: item.category,
        quantity: item.quantity,
        units: item.units,
        weight: [item.quantity || '', item.units].filter(Boolean).join(' '),
        brand: item.brand,
        qty: 1,
        image: item.image,
        price: item.price,
        product_code: item.product_code,
        productId: item.productId,
        brandId: item.brandId,
        financialId: item.financialId,
      },
    ]);
    setProductSearch('');
  };

  const removeDraftOnlyItem = (index) => {
    setDraftItems((items) => items.filter((_, itemIndex) => itemIndex !== index));
  };

  const deletePersistedItem = async (item) => {
    if (!selectedOrder?._id || !item?._id) return;
    const fromDispatch = selectedOrder.dispatchOrderId != null;
    if (!window.confirm(fromDispatch
      ? `Delete ${item.name || 'this item'}? It will also be removed from this order's dispatch.`
      : `Delete ${item.name || 'this item'} from the order?`)) return;

    try {
      await dispatch(
        deletePOSOrderItem({
          orderId: selectedOrder._id,
          itemId: item._id,
          remarks,
        })
      ).unwrap();
      await dispatch(fetchPOSOrderDetails(selectedOrder._id)).unwrap();
      await loadOrders();
    } catch (error) {
      window.alert(typeof error === 'string' ? error : error?.message || 'Failed to delete item');
    }
  };

  const saveOrder = async () => {
    if (!selectedOrder?._id) return;
    if(selectedOrder.dispatchOrderId!=null){
      if(savingDiscount||!canApplyOrderDiscount)return;
      setSavingDiscount(true);
      try{
        const response=await fetch(`${API_BASE_URL}/orders/pos/${selectedOrder._id}/discount`,{method:'POST',headers:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},body:JSON.stringify({discountPercentage:Number(discountPercentage)})});
        const result=await response.json();if(!response.ok)throw Error(result.message||'Unable to save discount');
        await loadOrders();await dispatch(fetchPOSOrderDetails(selectedOrder._id)).unwrap();
      }catch(error){window.alert(error.message);}finally{setSavingDiscount(false);}
      return;
    }
    if (draftItems.length === 0) {
      alert('Order must have at least one item.');
      return;
    }
    if (
      canApplyOrderDiscount &&
      (Number.isNaN(Number(discountPercentage || 0)) ||
        Number(discountPercentage || 0) < 0 ||
        Number(discountPercentage || 0) > MAX_ORDER_DISCOUNT_PERCENT)
    ) {
      setDiscountPercentage(String(MAX_ORDER_DISCOUNT_PERCENT));
      setDiscountMessage(APPROVED_DISCOUNT_MESSAGE);
      return;
    }

    try {
    await dispatch(
      updatePOSOrder({
        id: selectedOrder._id,
        orderItems: buildPayloadItems(draftItems),
        remarks,
        ...(canApplyOrderDiscount
          ? {
              discountPercentage: selectedDiscount.discountPercentage,
              discountAmount: selectedDiscount.discountAmount,
              totalPrice: selectedDiscount.totalAfterDiscount,
            }
          : {}),
      })
    ).unwrap();
    await loadOrders();
    await dispatch(fetchPOSOrderDetails(selectedOrder._id)).unwrap();
    } catch (error) {
      window.alert(typeof error === 'string' ? error : error?.message || 'Unable to save order changes.');
    }
  };

  const handleDiscountChange = (value) => {
    const nextDiscount = calculateOrderDiscount(selectedTotal, value);

    if (nextDiscount.clamped) {
      setDiscountPercentage(String(MAX_ORDER_DISCOUNT_PERCENT));
      setDiscountMessage(APPROVED_DISCOUNT_MESSAGE);
      return;
    }

    setDiscountPercentage(value);
    setDiscountMessage('');
  };

  const deleteOrder = async () => {
    if (!selectedOrder?._id) return;
    const id = selectedOrder.MK_order_id || selectedOrder._id;
    if (!window.confirm(`Delete order ${id}? This cannot be undone.`)) return;

    await dispatch(deletePOSOrder(selectedOrder._id)).unwrap();
    await loadOrders();
  };

  const rollbackOrder = async () => {
    if (!selectedOrder?._id || rollingBack) return;
    const linked = selectedOrder.dispatchOrderId != null;
    if (linked) {
      navigate(`/ecosystem/dispatch?dispatchOrderId=${encodeURIComponent(selectedOrder.dispatchOrderId)}`);
      return;
    }
    const id = selectedOrder.MK_order_id || selectedOrder._id;
    if (!window.confirm(linked
      ? `Rollback ${id}? This reverses the linked dispatch and returns stock to its source inventory. The payment history is retained.`
      : `Rollback order ${id}? This removes the bill and restores its sold quantities to ${selectedOrder.posLocation}. A rollback record is retained.`)) return;
    setRollingBack(true); setRollbackError('');
    try {
      await dispatch(rollbackPOSOrder({ id: selectedOrder._id, reason: 'Rollback requested in Order Management' })).unwrap();
      await loadOrders();
    } catch (error) { setRollbackError(typeof error === 'string' ? error : error.message || 'Order rollback failed'); }
    finally { setRollingBack(false); }
  };

  return (
    <StockManagerLayout>
      <main className="space-y-4">
        <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Order Management</h1>
              <p className="text-sm text-gray-500">
                Starts with current-day orders. Use filters when you need older orders.
              </p>
            </div>

            <div className="grid items-end gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
              <FilterInput
                label="From date"
                type="date"
                value={filters.fromDate}
                onChange={(value) => setFilters((prev) => ({ ...prev, fromDate: value }))}
              />
              <FilterInput
                label="To date"
                type="date"
                value={filters.toDate}
                onChange={(value) => setFilters((prev) => ({ ...prev, toDate: value }))}
              />
              <FilterInput
                label="Customer number"
                value={filters.customerNumber}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, customerNumber: value }))
                }
              />
              <FilterInput
                label="POS user"
                value={filters.posUser}
                onChange={(value) => setFilters((prev) => ({ ...prev, posUser: value }))}
              />
              <FilterInput
                label="Location"
                value={filters.location}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, location: value }))
                }
              />
              <label className="block text-xs font-semibold uppercase text-gray-500">
                Payment status
                <select
                  aria-label="Payment status"
                  value={filters.paymentStatus}
                  onChange={event => setFilters(prev => ({ ...prev, paymentStatus: event.target.value }))}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal normal-case text-gray-900"
                >
                  <option value="">All payment statuses</option>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partially paid</option>
                  <option value="pay-later">Pay Later (outstanding)</option>
                </select>
              </label>
              <label className="block text-xs font-semibold uppercase text-gray-500">
                Dispatch to
                <select
                  aria-label="Dispatch to"
                  value={filters.destinationType}
                  onChange={event => setFilters(prev => ({ ...prev, destinationType: event.target.value }))}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal normal-case text-gray-900"
                >
                  <option value="">All destinations</option>
                  <option value="outlet">Outlet</option>
                  <option value="customer">Customer</option>
                  <option value="vendor">Vendor</option>
                  <option value="internal_packing">Internal Packing</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </label>
              <button
                type="button"
                onClick={loadOrders}
                className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white hover:bg-blue-800"
              >
                <Search size={16} />
                Search
              </button>
            </div>
          </div>
        </section>

        {ordersError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            {ordersError}
          </div>
        ) : null}

        <section className="grid items-start gap-4 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <h2 className="font-bold text-gray-900">{ordersHeading}</h2>
                <p className="text-xs text-gray-500">
                  {ordersLoading ? 'Loading orders…' : searchPending
                    ? 'Filters changed. Click Search to load matching orders.'
                    : `${visibleOrders.length} matching orders.${loadedQuery?.mode === 'custom' ? ` ${loadedQuery.from} to ${loadedQuery.to}.` : ' Select dates to include older orders.'}`}
                </p>
              </div>
              <button
                type="button"
                onClick={loadOrders}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw size={15} />
                Refresh
              </button>
            </div>

            <div className="pb-2">
              {ordersLoading ? (
                <EmptyState text="Loading orders..." />
              ) : visibleOrders.length === 0 ? (
                <EmptyState text="No orders found." />
              ) : (
                <div className="divide-y divide-gray-100">
                  {visibleOrders.map((order) => (
                    <button
                      type="button"
                      key={order._id}
                      onClick={() => openOrder(order._id)}
                      className={`block w-full px-4 py-3 text-left hover:bg-blue-50 ${
                        selectedOrder?._id === order._id ? 'bg-blue-50' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-bold text-gray-900">
                            {order.orderId || order.MK_order_id || order._id}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {formatDateTime(order.createdAt)}
                          </p>
                          <p className="mt-1 text-sm text-gray-700">
                            {order.phoneNo || order.user?.phoneNo || '-'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            {money(getDisplayedOrderDiscount(order).totalAfterDiscount)}
                          </p>
                          {getDisplayedOrderDiscount(order).discountAmount > 0 ? (
                            <p className="mt-1 text-xs font-semibold text-green-700">
                              Disc {money(getDisplayedOrderDiscount(order).discountAmount)}
                            </p>
                          ) : null}
                          <p className="mt-1 text-xs text-gray-500">
                            {order.posUserName || '-'}
                          </p>
                        </div>
                      </div>
                      <p className="mt-2 truncate text-xs text-gray-500">
                        {order.posLocation || '-'}
                      </p>
                      <OrderPaymentStatus order={order} compact />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            {!selectedOrder ? (
              <EmptyState text="Select an order to edit." />
            ) : detailsLoading ? (
              <EmptyState text="Loading order details..." />
            ) : (
              <div className="space-y-4 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedOrder.orderId || selectedOrder.MK_order_id || selectedOrder._id}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {formatDateTime(selectedOrder.createdAt)} |{' '}
                      {selectedOrder.phoneNo || selectedOrder.user?.phoneNo || '-'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedOrder.posUserName || '-'} |{' '}
                      {selectedOrder.posLocation || '-'}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <select aria-label="Print product language" value={printLanguage} onChange={event => setPrintLanguage(event.target.value)} className="h-10 rounded border px-2">
                      <option value="english">English products</option><option value="telugu">తెలుగు products</option>
                    </select>
                    <button type="button" disabled={printCatalogLoading} onClick={() => printOrder({ ...selectedOrder, items: normalizeDetailItems(selectedOrder, catalog) }, { language: printLanguage, catalogBarcodes: printCatalog })} className="h-10 rounded border px-4 font-semibold disabled:opacity-50">{printCatalogLoading ? 'Loading names…' : 'Print order'}</button>
                    <button
                      type="button"
                      onClick={saveOrder}
                      disabled={saving || savingDiscount || rollingBack || (selectedOrder.dispatchOrderId != null && (!canApplyOrderDiscount || selectedOrder.isPosSettled)) || (selectedOrder.fulfillment && selectedOrder.fulfillment.state !== 'released')}
                      className="inline-flex h-10 items-center gap-2 rounded-lg bg-green-700 px-4 text-sm font-semibold text-white hover:bg-green-800 disabled:bg-gray-400"
                    >
                      <Save size={16} />
                      {saving || savingDiscount ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={deleteOrder}
                      disabled={saving || rollingBack || selectedOrder.dispatchOrderId != null || (selectedOrder.fulfillment && selectedOrder.fulfillment.state !== 'released')}
                      className="inline-flex h-10 items-center gap-2 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400"
                    >
                      <Trash2 size={16} />
                      Delete Order
                    </button>
                    <button type="button" onClick={rollbackOrder}
                      disabled={saving || rollingBack || selectedOrder.isPosSettled || (selectedOrder.fulfillment && selectedOrder.fulfillment.state !== 'released')}
                      className="inline-flex h-10 items-center gap-2 rounded-lg bg-amber-700 px-4 text-sm font-semibold text-white disabled:bg-gray-400">
                      <RefreshCw size={16} />{rollingBack ? 'Rolling back…' : 'Rollback Order'}
                    </button>
                  </div>
                </div>

                <OrderPaymentStatus order={selectedOrder} />
                {['ONLINE','ANDROID'].includes(selectedOrder.source) && <OnlineOrderFulfillment key={selectedOrder._id}
                  order={selectedOrder} token={token} onChanged={async () => {
                    await dispatch(fetchPOSOrderDetails(selectedOrder._id)).unwrap();
                    await loadOrders();
                  }} />}
                {rollbackError && <p role="alert" className="text-red-700">{rollbackError}</p>}
                {selectedOrder.dispatchOrderId != null && <p className="text-sm text-gray-600">Linked dispatch order. Save applies the manual order discount only. Record payments here; manage items and cancellations in Dispatch.</p>}
                <button type="button" disabled={saving || selectedOrder.isPosSettled} onClick={() => setEditingPayment(true)} className="rounded border border-blue-300 px-3 py-2 font-semibold text-blue-700 disabled:opacity-50">{selectedOrder.isPosSettled ? 'Payment settled' : 'Edit payment'}</button>
                {editingPayment && <EditOrderPaymentModal order={selectedOrder} token={token} onCancel={() => setEditingPayment(false)} onSaved={async () => {
                  setEditingPayment(false);
                  await dispatch(fetchPOSOrderDetails(selectedOrder._id)).unwrap();
                  await loadOrders();
                }} />}
                <div className="grid gap-3 lg:grid-cols-[1fr_260px]">
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-700">
                      Remarks
                    </span>
                    <textarea
                      value={remarks}
                      onChange={(event) => setRemarks(event.target.value)}
                      placeholder="Optional. Backend adds default added/removed item remarks with date."
                      className="mt-1 h-20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </label>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-semibold text-gray-500">Items total</p>
                    <p className="mt-1 text-xl font-bold text-gray-900">
                      {money(selectedTotal)}
                    </p>
                    {canApplyOrderDiscount ? (
                      <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
                        <label className="block">
                          <span className="text-xs font-bold uppercase text-gray-500">
                            Discount %
                          </span>
                          <input
                            type="number"
                            min="0"
                            max={MAX_ORDER_DISCOUNT_PERCENT}
                            step="0.01"
                            value={discountPercentage}
                            onChange={(event) => handleDiscountChange(event.target.value)}
                            className="mt-1 h-9 w-full rounded-lg border border-gray-300 px-2 text-right text-sm outline-none focus:border-blue-500"
                          />
                        </label>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between text-green-700">
                            <span>Discount</span>
                            <strong>{money(selectedDiscount.discountAmount)}</strong>
                          </div>
                          <div className="flex justify-between text-gray-900">
                            <span>Payable</span>
                            <strong>{money(selectedDiscount.totalAfterDiscount)}</strong>
                          </div>
                          {discountMessage ? (
                            <p className="rounded-md bg-orange-50 px-2 py-1 text-xs font-semibold text-orange-700">
                              {discountMessage}
                            </p>
                          ) : null}
                          <p className="text-xs font-semibold text-gray-500">
                            Max {MAX_ORDER_DISCOUNT_PERCENT}%. Rounded down to rupees.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-gray-500">
                        Payable: {money(selectedOrder.totalPrice || selectedTotal)}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-gray-500">{draftItems.length} item(s)</p>
                  </div>
                </div>

                <OrderProductSuggestions catalog={catalog} search={productSearch} onSearch={setProductSearch}
                  onSelect={addCatalogItem} loading={productsLoading} error={productsError} outlet={orderOutlet}
                  onReload={() => dispatch(fetchAllProductsFresh(token))} />
                <div className="overflow-auto rounded-lg border border-gray-200">
                  <table className="min-w-[980px] w-full text-sm">
                    <thead className="bg-gray-100 text-left text-xs font-bold uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-3">Item</th>
                        <th className="px-3 py-3">Category</th>
                        <th className="px-3 py-3">Brand</th>
                        <th className="px-3 py-3">Weight</th>
                        <th className="px-3 py-3">Units</th>
                        <th className="px-3 py-3 text-right">Order Qty</th>
                        <th className="px-3 py-3 text-right">Price</th>
                        <th className="px-3 py-3 text-right">Amount</th>
                        <th className="px-3 py-3 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {draftItems.map((item, index) => (
                        <tr key={item._rowKey}>
                          <td className="px-3 py-2">
                            <TextField
                              value={item.name}
                              onChange={(value) => updateItem(index, 'name', value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TextField
                              value={item.category}
                              onChange={(value) => updateItem(index, 'category', value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TextField
                              value={item.brand}
                              onChange={(value) => updateItem(index, 'brand', value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <NumberField
                              value={item.quantity}
                              onChange={(value) => updateItem(index, 'quantity', value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TextField
                              value={item.units}
                              onChange={(value) => updateItem(index, 'units', value)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <NumberField
                              value={item.qty}
                              onChange={(value) => updateItem(index, 'qty', value)}
                              align="right"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <NumberField
                              value={item.price}
                              onChange={(value) => updateItem(index, 'price', value)}
                              align="right"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-gray-900">
                            {money(Number(item.qty || 0) * Number(item.price || 0))}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                item._id ? deletePersistedItem(item) : removeDraftOnlyItem(index)
                              }
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-red-600 hover:bg-red-50"
                              aria-label="Delete item"
                            >
                              {item._id ? <Trash2 size={16} /> : <X size={16} />}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {asArray(selectedOrder.remarks).length ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-2 flex items-center gap-2 font-bold text-gray-900">
                      <Edit3 size={16} />
                      Remarks history
                    </div>
                    <div className="max-h-36 space-y-2 overflow-auto text-sm text-gray-700">
                      {selectedOrder.remarks.map((remark, index) => (
                        <p key={index}>
                          {typeof remark === 'string'
                            ? remark
                            : [remark.message || remark.text, formatDateTime(remark.createdAt)]
                                .filter(Boolean)
                                .join(' | ')}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>
      </main>
    </StockManagerLayout>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase text-gray-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 h-10 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    />
  </label>
);

const TextField = ({ value, onChange }) => (
  <input
    type="text"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm outline-none focus:border-blue-500"
  />
);

const NumberField = ({ value, onChange, align = 'left' }) => (
  <input
    type="number"
    min="0"
    step="0.01"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className={`h-9 w-full rounded-lg border border-gray-300 px-2 text-sm outline-none focus:border-blue-500 ${
      align === 'right' ? 'text-right' : ''
    }`}
  />
);

const EmptyState = ({ text }) => (
  <div className="flex min-h-[220px] items-center justify-center p-6 text-center text-sm font-semibold text-gray-500">
    {text}
  </div>
);

export default OrderManagementPage;

